import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { DataTable, type Column } from '@/components/DataTable'
import {
  formatMoney,
  getCustomers,
  MOVEMENT_LABELS,
  type CustomerRow,
} from '@/features/analytics/api'
import { ChurnPanel } from '@/features/analytics/insights'
import { ChartCard, Kpi, PanelCard, SegmentChart, WindowPicker } from '@/features/analytics/panels'

const MOVEMENT_STYLE: Record<CustomerRow['movement'], string> = {
  up: 'bg-success/10 text-success',
  down: 'bg-danger/10 text-danger',
  hold: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
}

/**
 * RFM scoring and the tier moves it implies.
 *
 * Recency, frequency and monetary value are each scored 1–5 against the rest of the book,
 * not against a fixed rupee or dollar threshold. A threshold written into the code is wrong
 * in a different currency, wrong after a price rise, and wrong for a second company on the
 * same install; a rank is none of those.
 *
 * The tier on file is left alone. This screen suggests, and somebody with the customer
 * record open decides -- a tier is a commercial promise, sometimes made by hand for a
 * reason no dataset here can see.
 */
export function CustomerAnalytics() {
  const [days, setDays] = useState(365)

  const customers = useQuery({
    queryKey: ['analytics', 'customers', days, 100],
    queryFn: () => getCustomers({ days, limit: 100 }),
    retry: false,
  })

  const rows = customers.data?.rows ?? []
  const movements = customers.data?.movements ?? []
  const nameOf = (row: CustomerRow) => row.customer_name ?? row.customer_id.slice(0, 8)

  const columns: Column<CustomerRow>[] = [
    {
      key: 'customer',
      header: 'Customer',
      className: 'font-medium',
      sortValue: nameOf,
      cell: nameOf,
    },
    {
      key: 'segment',
      header: 'Segment',
      className: 'text-slate-500 dark:text-slate-400',
      sortValue: (row) => row.segment,
      cell: (row) => row.segment,
    },
    {
      key: 'recency',
      header: 'Last bought',
      className: 'text-right tabular-nums',
      headerClassName: 'text-right',
      sortValue: (row) => row.recency_days,
      cell: (row) => `${row.recency_days} d ago`,
    },
    {
      key: 'frequency',
      header: 'Orders',
      className: 'text-right tabular-nums',
      headerClassName: 'text-right',
      sortValue: (row) => row.frequency,
      cell: (row) => row.frequency.toLocaleString(),
    },
    {
      key: 'monetary',
      header: 'Spend',
      className: 'text-right tabular-nums',
      headerClassName: 'text-right',
      sortValue: (row) => row.monetary,
      cell: (row) => formatMoney(row.monetary),
    },
    {
      key: 'rfm',
      header: 'R / F / M',
      className: 'text-right tabular-nums text-slate-500 dark:text-slate-400',
      headerClassName: 'text-right',
      sortValue: (row) => row.recency_score * 100 + row.frequency_score * 10 + row.monetary_score,
      cell: (row) => `${row.recency_score} / ${row.frequency_score} / ${row.monetary_score}`,
    },
    {
      key: 'tier',
      header: 'Tier',
      className: 'text-right',
      headerClassName: 'text-right',
      sortValue: (row) => row.movement,
      cell: (row) => (
        <span className="inline-flex items-center gap-2">
          <span className="text-slate-500 dark:text-slate-400">{row.tier ?? '—'}</span>
          {row.movement !== 'hold' && (
            <span
              className={`rounded px-2 py-0.5 text-xs font-medium ${MOVEMENT_STYLE[row.movement]}`}
            >
              {MOVEMENT_LABELS[row.movement]} to {row.suggested_tier}
            </span>
          )}
        </span>
      ),
    },
  ]

  const champions = customers.data?.segments.find((slice) => slice.segment === 'champions')
  const atRisk = customers.data?.segments.find((slice) => slice.segment === 'at risk')

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Customer analytics</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Recency, frequency and spend, scored against the rest of the book.
          </p>
        </div>
        <WindowPicker days={days} onChange={setDays} asOf={customers.data?.as_of} />
      </header>

      <div className="grid gap-4 tablet:grid-cols-2 desktop:grid-cols-4">
        <Kpi
          label="Customers"
          value={customers.data ? customers.data.customers.toLocaleString() : '—'}
          hint={`bought in the last ${days} days`}
          loading={customers.isLoading}
        />
        <Kpi
          label="Champions"
          value={champions ? champions.customers.toLocaleString() : '—'}
          hint={champions ? `${formatMoney(champions.monetary)} of spend` : 'None yet'}
          loading={customers.isLoading}
        />
        <Kpi
          label="At risk"
          value={atRisk ? atRisk.customers.toLocaleString() : '—'}
          hint={atRisk ? `${formatMoney(atRisk.monetary)} to win back` : 'Nobody drifting'}
          loading={customers.isLoading}
        />
        <Kpi
          label="Tier moves"
          value={customers.data ? movements.length.toLocaleString() : '—'}
          hint="spend and tier disagree"
          loading={customers.isLoading}
        />
      </div>

      <ChartCard
        title="Customers by segment"
        description="Count of customers and what each segment is worth."
        isLoading={customers.isLoading}
        error={customers.error}
        isEmpty={!customers.data?.segments.length}
        emptyMessage="Nobody bought anything in this window."
        height={280}
      >
        <SegmentChart segments={customers.data?.segments ?? []} />
      </ChartCard>

      <ChurnPanel days={days} />

      <PanelCard
        title="Suggested tier moves"
        description="Where spend puts a customer on a different tier than the one on file. Nothing is changed here."
        isLoading={customers.isLoading}
        error={customers.error}
        isEmpty={!movements.length}
        emptyMessage="Every customer is on the tier their spend puts them on."
      >
        <DataTable
          rows={movements}
          columns={columns}
          getRowId={(row) => row.customer_id}
          caption="Customers whose spend suggests a different tier"
          emptyMessage="Every customer is on the tier their spend puts them on."
        />
      </PanelCard>

      <DataTable
        rows={rows}
        columns={columns}
        getRowId={(row) => row.customer_id}
        caption="Customers scored on recency, frequency and spend"
        isLoading={customers.isLoading}
        error={customers.error}
        emptyMessage="Nobody bought anything in this window."
        search={{
          placeholder: 'Search customers',
          matches: (row, query) => nameOf(row).toLowerCase().includes(query),
        }}
      />
    </div>
  )
}
